import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface ListItemType {
  type: string;
  displayText: string;
  format?: string;
  visible?: boolean;
  value: any;
}
export interface PolicyDetailType {
  policyNumber: string;
  companyCode: string;
  state: string;
  product: string;
  policyStatusCode: number;
  policyStatus: string;
  effectiveDate: string;
  endDate: string;
  policyHolders: {
    id: string;
    type: string;
    order: number;
    firstName: string;
    lastName: string;
    email: string;
    primaryPhoneNumber: string;
  }[];
  mailingAddress: any;
  propertyDetails: any[];
  enabledPayment: boolean;
  // billingDetails:
  billingDetails: {
    premium: {
      currentPremium: ListItemType | undefined;
      initialPremium: ListItemType | undefined;
      balanceDue: ListItemType | undefined;
      isDue: boolean;
    };
    information: {
      nextPayment: ListItemType | undefined;
      paymentDue: ListItemType | undefined;
      billPlan: ListItemType | undefined;
      billTo: ListItemType | undefined;
      cashReceived: ListItemType | undefined;
      effectiveDate: ListItemType | undefined;
    };
  };
  // coverageDetails:
  coverageDetails: {
    coverageLimits: { [key: string]: ListItemType };
    coverageOptions: { [key: string]: ListItemType };
    deductibles: { [key: string]: ListItemType };
    surcharges: { [key: string]: ListItemType };
    underwritingAnswers: { [key: string]: ListItemType };
    combinedCoverages: { [key: string]: ListItemType };
  };

  agent: {
    primaryPhoneNumber: string;
    emailAddress: string;
    faxNumber: string;
    firstName: string;
    lastName: string;
    physicalAddress: any;
    mailingAddress: any;
    errorCode: string;
    agencyName: string;
    agencyCode: string;
  };
  docs: {
    id: string;
    fileName: string;
    policyNumber: string;
    fileUrl: string;
  }[];
  payments: {
    date: string;
    amount: string;
    type: string;
    description: string;
  }[];
  loading: boolean;
  paymentsLoading?: boolean;
  error: string;
}
const initialState: PolicyDetailType = {
  policyNumber: "",
  companyCode: "",
  state: "",
  product: "",
  policyStatusCode: 0,
  policyStatus: "",
  effectiveDate: "",
  endDate: "",
  policyHolders: [],
  propertyDetails: [],
  mailingAddress: [],
  enabledPayment: false,
  billingDetails: {
    premium: {
      currentPremium: undefined,
      initialPremium: undefined,
      balanceDue: undefined,
      isDue: false,
    },
    information: {
      nextPayment: undefined,
      paymentDue: undefined,
      billPlan: undefined,
      billTo: undefined,
      cashReceived: undefined,
      effectiveDate: undefined,
    },
  },
  coverageDetails: {
    coverageLimits: {},
    coverageOptions: {},
    deductibles: {},
    surcharges: {},
    underwritingAnswers: {},
    combinedCoverages: {},
  },

  agent: {
    primaryPhoneNumber: "",
    emailAddress: "",
    faxNumber: "",
    firstName: "",
    lastName: "",
    physicalAddress: "",
    mailingAddress: "",
    errorCode: "",
    agencyName: "",
    agencyCode: "",
  },
  docs: [],
  payments: [],
  loading: false,
  error: "",
  paymentsLoading: false,
};

export const policyDetailSlice = createSlice({
  name: "PolicyDetail",
  initialState,
  reducers: {
    updatePolicyDetails: (state, { payload }: PayloadAction<any>) => {
      if (payload) {
        state.policyNumber = payload.policyNumber;
        state.companyCode = payload.companyCode;
        state.state = payload.state;
        state.product = payload.product;
        state.effectiveDate = payload.effectiveDate;
        state.endDate = payload.endDate;
        state.mailingAddress = payload.mailingAddress;
        state.policyHolders = payload.policyHolders;
        state.propertyDetails = payload.propertyDetails;
        state.enabledPayment = payload.enabledPayment;
        state.billingDetails = {
          premium: {
            currentPremium: payload?.billingDetails?.premium?.currentPremium,
            initialPremium: payload?.billingDetails?.premium?.initialPremium,
            balanceDue: payload?.billingDetails?.premium?.balanceDue,
            isDue: payload?.billingDetails?.premium?.isDue,
          },
          information: {
            nextPayment: payload?.billingDetails?.information?.nextPayment,
            paymentDue: payload?.billingDetails?.information?.paymentDue,
            billPlan: payload?.billingDetails?.information?.billPlan,
            billTo: payload?.billingDetails?.information?.billTo,
            cashReceived: payload?.billingDetails?.information?.cashReceived,
            effectiveDate: payload?.billingDetails?.information?.effectiveDate,
          },
        };
        state.coverageDetails = {
          coverageLimits: payload.coverageDetails?.coverageLimits,
          coverageOptions: payload.coverageDetails?.coverageOptions,
          deductibles: payload.coverageDetails?.deductibles,
          surcharges: payload.coverageDetails?.surcharges,
          underwritingAnswers: payload.coverageDetails?.underwritingAnswers,
          combinedCoverages: payload.coverageDetails?.combinedCoverages,
        };

        state.agent = {
          primaryPhoneNumber: payload?.agent?.primaryPhoneNumber,
          emailAddress: payload?.agent?.emailAddress,
          faxNumber: payload?.agent?.faxNumber,
          firstName: payload?.agent?.firstName,
          lastName: payload?.agent?.lastName,
          physicalAddress: payload?.agent?.physicalAddress,
          mailingAddress: payload?.agent?.mailingAddress,
          errorCode: payload?.agent?.errorCode,
          agencyName: payload?.agent?.agencyName,
          agencyCode: payload?.agent?.agencyCode,
        };
      }
    },

    updatePreviousPayments: (state, { payload }: PayloadAction<any>) => {
      if (payload) {
        state.payments = payload?.map((payment: any) => ({
          date: payment.date,
          amount: payment.amount,
          type: payment.type,
          description: payment.description,
        }));
      }
    },
    setPaymentsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.paymentsLoading = payload;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state, { payload }: PayloadAction<string>) => {
      state.loading = false;
      state.error = payload;
    },
    resetPolicyDetail: () => initialState,
  },
});

export const {
  resetPolicyDetail,
  setLoading,
  setError,
  updatePolicyDetails,
  updatePreviousPayments,
  setPaymentsLoading,
} = policyDetailSlice.actions;

export default policyDetailSlice.reducer;
